import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';
import Layout from './Layout';
import '../styles/InstallerTimesheetPage.css';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    DatePicker,
    Empty,
    Input,
    Progress,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Statistic,
    Tabs,
    Tag,
    Typography,
    message,
    Descriptions,
    Modal,
    Table,
} from 'antd';

import {
    BarChartOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    InboxOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';

import { Link } from 'react-router-dom';

import InstallerOutboxBadge from './InstallerOutboxBadge';
import InstallerTmcPanel from './InstallerTmcPanel';

import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import dayjs from 'dayjs';


const {
    RangePicker
} = DatePicker;

const {
    Text,
    Title
} = Typography;

const DEFAULT_DATE_RANGE = [
    dayjs().startOf('month'),
    dayjs().endOf('month')
];

const TASK_STAGE_COLORS = {
    'новое':
        '#999999',

    'назначено':
        '#33ccff',

    'принято исполнителем':
        '#6666FF',

    'в дороге на объект':
        '#009999',

    'на объекте':
        '#ffff33',

    'работы завершены':
        '#ff9933',

    'выполнено (заполнен отчёт)':
        '#33cc33',

    'возращено исполнителю':
        '#ff9933',

    /**
     * Добавлен также правильный вариант написания,
     * если внешний сервис исправит опечатку.
     */
    'возвращено исполнителю':
        '#ff9933',

    'проверено':
        '#20f70c',

    'осмечено':
        '#FF1493',

    'отправлено заказчику':
        '#9400D3',

    'принято заказчиком':
        '#7FC7FF',

    'на контроле':
        '#611111'
};

/**
 * Форматирует минуты.
 */
const formatMinutes = minutesValue => {
    const minutes =
        Number(minutesValue) || 0;

    const hours =
        Math.floor(minutes / 60);

    const remainingMinutes =
        minutes % 60;

    if (!hours) {
        return `${remainingMinutes} мин`;
    }

    if (!remainingMinutes) {
        return `${hours} ч`;
    }

    return `${hours} ч ${remainingMinutes} мин`;
};

/**
 * Форматирует денежную сумму.
 */
const formatMoney = amount => {
    if (
        amount === null ||
        amount === undefined
    ) {
        return '—';
    }

    return `${Number(amount).toLocaleString('ru-RU')} ₽`;
};
const getTaskStageColor = stage => {
    return (
        TASK_STAGE_COLORS[
            normalizeTaskStage(stage)
        ] ||
        '#1677ff'
    );
};

const getContrastTextColor = background => {
    const hex =
        String(background || '')
            .replace('#', '');

    if (hex.length !== 6) {
        return '#ffffff';
    }

    const red =
        parseInt(
            hex.substring(0, 2),
            16
        );

    const green =
        parseInt(
            hex.substring(2, 4),
            16
        );

    const blue =
        parseInt(
            hex.substring(4, 6),
            16
        );

    const brightness =
        (
            red * 299 +
            green * 587 +
            blue * 114
        ) / 1000;

    return brightness > 160
        ? '#262626'
        : '#ffffff';
};

const normalizeTaskStage = value => {
    return String(
        value ||
        ''
    )
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ');
};
const TaskStageTag = ({
    stage
}) => {
    const stageTitle =
        stage ||
        'Без этапа';

    const background =
        getTaskStageColor(
            stageTitle
        );

    return (
        <Tag
            style={{
                display:
                    'inline-flex',

                maxWidth:
                    '100%',

                minWidth:
                    0,

                height:
                    'auto',

                marginInlineEnd:
                    0,

                boxSizing:
                    'border-box',

                background,

                borderColor:
                    background,

                color:
                    getContrastTextColor(
                        background
                    ),

                fontWeight:
                    600,

                whiteSpace:
                    'normal',

                overflowWrap:
                    'anywhere',

                wordBreak:
                    'break-word',

                lineHeight:
                    1.35,

                padding:
                    '3px 8px',

                borderRadius:
                    6
            }}
        >
            {stageTitle}
        </Tag>
    );
};

const normalizeComparableText = value => {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ');
};

const getInvoiceCounterpartyName = invoice => {
    const value =
        invoice?.counterparty_name ??
        invoice?.counterparty;

    if (
        value &&
        typeof value === 'object'
    ) {
        return (
            value.label ||
            value.name ||
            value.title ||
            ''
        );
    }

    return String(value || '');
};

const formatInvoiceNumber = (
    value,
    fractionDigits = 2
) => {
    const number =
        Number(
            String(value ?? 0)
                .replace(',', '.')
        );

    if (!Number.isFinite(number)) {
        return '—';
    }

    return number.toLocaleString(
        'ru-RU',
        {
            minimumFractionDigits:
                fractionDigits,
            maximumFractionDigits:
                fractionDigits
        }
    );
};

/**
 * Возвращает первое непустое значение.
 */
const firstTaskValue = (
    ...values
) => {
    const value =
        values.find(item =>
            item !== undefined &&
            item !== null &&
            String(item).trim() !== ''
        );

    return value ??
        null;
};

const isGenerationWorkType = value => {
    const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ');

    return (
        normalized === 'генерация' ||
        normalized.includes('генерация')
    );
};

const InstallerTimesheetPage = () => {
    const [
        activeTab,
        setActiveTab
    ] = useState('tasks');

    const [
        analyticsMode,
        setAnalyticsMode
    ] = useState('daily');

    const [
        quickPeriod,
        setQuickPeriod
    ] = useState('month');

    const [
        dateRange,
        setDateRange
    ] = useState(DEFAULT_DATE_RANGE);

    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        currentUser,
        setCurrentUser
    ] = useState(null);

    const [
    selectedTask,
    setSelectedTask
] = useState(null);

const [
    taskModalOpen,
    setTaskModalOpen
] = useState(false);

    const [
        userCounterparties,
        setUserCounterparties
    ] = useState([]);

    const [
        filters,
        setFilters
    ] = useState({
        search: '',
        ttNumber: undefined,
        workType: undefined,
        status: undefined,
    });

    const [
    externalTaskData,
    setExternalTaskData
] = useState(null);

const [
    taskModalLoading,
    setTaskModalLoading
] = useState(false);

    const [
        report,
        setReport
    ] = useState({
        summary: {
    tasksCount:
        0,

    workingDays:
        0,

    workedHours:
        0,

    averageHoursPerDay:
        0,

    workedMinutes:
        0,

    unfinishedCount:
        0,

    estimatedCount:
        0,

    preliminaryCount:
        0,

    finalCount:
        0
},

        tasks: [],
        unfinished: [],
        estimated: [],
        preliminary: [],
        final: [],

        charts: {
            daily: [],
            weekly: [],
            monthly: []
        },

        options: {
            ttNumbers: [],
            workTypes: [],
            statuses: []
        }
    });

    const averageHoursStats = useMemo(() => {
    const tasks = Array.isArray(report.tasks)
        ? report.tasks
        : [];

    const regularTasks = tasks.filter(
        task =>
            !isGenerationWorkType(
                task?.workType
            )
    );

    const generationTasks = tasks.filter(
        task =>
            isGenerationWorkType(
                task?.workType
            )
    );

    const getWorkedMinutes = task => {
        const directMinutes =
            Number(task?.workedMinutes);

        if (
            Number.isFinite(directMinutes) &&
            directMinutes > 0
        ) {
            return directMinutes;
        }

        const workedHours =
            Number(task?.workedHours);

        if (
            Number.isFinite(workedHours) &&
            workedHours > 0
        ) {
            return workedHours * 60;
        }

        return 0;
    };

    /*
     * Строго используем workingDate из backend.
     * Именно по нему считается общая карточка дней.
     */
    const getTaskDay = task => {
        return task?.workingDate
            ? String(task.workingDate)
            : null;
    };

    const calculateGroup = groupTasks => {
        const tasksWithHours =
            groupTasks.filter(
                task =>
                    getWorkedMinutes(task) > 0 &&
                    getTaskDay(task)
            );

        const totalMinutes =
            tasksWithHours.reduce(
                (sum, task) =>
                    sum +
                    getWorkedMinutes(task),
                0
            );

        const workingDates =
            new Set(
                tasksWithHours.map(
                    getTaskDay
                )
            );

        const workingDays =
            workingDates.size;

        const totalHours =
            totalMinutes / 60;

        const averageHoursPerDay =
            workingDays > 0
                ? totalHours / workingDays
                : 0;

        return {
            tasksCount:
                tasksWithHours.length,

            workingDays,

            totalMinutes,

            totalHours,

            averageHoursPerDay,

            workingDates:
                Array.from(workingDates)
        };
    };

    return {
        regular:
            calculateGroup(regularTasks),

        generation:
            calculateGroup(generationTasks)
    };
}, [report.tasks]);

    const selectedTaskStage =
    firstTaskValue(
        externalTaskData
            ?.status_name,

        externalTaskData
            ?.status
            ?.name,

        selectedTask
            ?.status
    );

const isSelectedTaskEstimated =
    normalizeTaskStage(
        selectedTaskStage
    ) === 'осмечено';

const currentUserCounterpartyNames =
    useMemo(() => {
        return new Set(
            (
                Array.isArray(
                    userCounterparties
                )
                    ? userCounterparties
                    : []
            )
                .map(counterparty =>
                    normalizeComparableText(
                        counterparty?.name
                    )
                )
                .filter(Boolean)
        );
    }, [
        userCounterparties
    ]);

const selectedTaskExpenseRows =
    useMemo(() => {
        const rows =
            Array.isArray(
                selectedTask
                    ?.expenseVdrInvoices
            )
                ? selectedTask
                    .expenseVdrInvoices
                : [];

        /*
         * Показываем только строки контрагентов,
         * привязанных к текущему пользователю.
         */
        return rows.filter(invoice => {
            const counterpartyName =
                normalizeComparableText(
                    getInvoiceCounterpartyName(
                        invoice
                    )
                );

            if (!counterpartyName) {
                return false;
            }

            return currentUserCounterpartyNames
                .has(counterpartyName);
        });
    }, [
        selectedTask,
        currentUserCounterpartyNames
    ]);

    /**
 * Задачи, по которым исполнитель заполнил отчёт,
 * но которые ещё не перешли на этап «Осмечено».
 */
const notEstimatedTasks =
    useMemo(() => {
        const tasks =
            Array.isArray(report.tasks)
                ? report.tasks
                : [];

        return tasks.filter(task => {
            return (
                normalizeTaskStage(
                    task?.status
                ) ===
                'выполнено (заполнен отчет)'
            );
        });
    }, [
        report.tasks
    ]);

    const expenseInvoiceColumns =
    useMemo(() => [
        {
            title:
                'Контрагент',

            dataIndex:
                'counterparty',

            key:
                'counterparty',

            width:
                190,

            render:
                (_, invoice) =>
                    getInvoiceCounterpartyName(
                        invoice
                    ) || '—'
        },
        {
            title:
                'Статья',

            dataIndex:
                'article',

            key:
                'article',

            width:
                180,

            render:
                value =>
                    formatTaskValue(value)
        },
        {
            title:
                'Наименование',

            dataIndex:
                'rate_name',

            key:
                'rate_name',

            width:
                280,

            render:
                value =>
                    formatTaskValue(value)
        },
        {
            title:
                'Кол-во',

            dataIndex:
                'quantity',

            key:
                'quantity',

            width:
                90,

            align:
                'right',

            render:
                value =>
                    formatInvoiceNumber(
                        value,
                        2
                    )
        },
        {
            title:
                'Цена',

            dataIndex:
                'price',

            key:
                'price',

            width:
                110,

            align:
                'right',

            render:
                value =>
                    formatInvoiceNumber(
                        value,
                        2
                    )
        },
        {
            title:
                'Ед.',

            dataIndex:
                'unit',

            key:
                'unit',

            width:
                70,

            render:
                value =>
                    formatTaskValue(value)
        },
        {
            title:
                '%',

            dataIndex:
                'percent',

            key:
                'percent',

            width:
                70,

            align:
                'right',

            render:
                value =>
                    `${
                        formatInvoiceNumber(
                            value ?? 100,
                            2
                        )
                    }%`
        },
        {
            title:
                'Сумма',

            dataIndex:
                'amount',

            key:
                'amount',

            width:
                130,

            align:
                'right',

            render:
                value => (
                    <Text strong>
                        {formatInvoiceNumber(
                            value,
                            2
                        )} ₽
                    </Text>
                )
        },
        {
            title:
                'Примечание',

            dataIndex:
                'note',

            key:
                'note',

            width:
                170,

            render:
                value =>
                    formatTaskValue(value)
        },
        {
            title:
                'Штраф %',

            dataIndex:
                'penalty',

            key:
                'penalty',

            width:
                90,

            align:
                'right',

            render:
                value =>
                    `${
                        formatInvoiceNumber(
                            value || 0,
                            2
                        )
                    }%`
        },
        {
            title:
                'Учитывать',

            dataIndex:
                'include',

            key:
                'include',

            width:
                90,

            align:
                'center',

            render:
                value => (
                    <Tag
                        color={
                            value === false ||
                            value === 0 ||
                            value === '0'
                                ? 'default'
                                : 'green'
                        }
                    >
                        {
                            value === false ||
                            value === 0 ||
                            value === '0'
                                ? 'Нет'
                                : 'Да'
                        }
                    </Tag>
                )
        }
    ], []);

    const requestParams = useMemo(() => {
        return {

            dateFrom:
                dateRange?.[0]
                    ?.startOf('day')
                    .toISOString(),

            dateTo:
                dateRange?.[1]
                    ?.endOf('day')
                    .toISOString(),

            search:
                filters.search ||
                undefined,

            ttNumber:
                filters.ttNumber,

            workType:
                filters.workType,

            status:
                filters.status
        };
    }, [
        dateRange,
        filters,
        userCounterparties
    ]);

 

    /**
     * Загрузка отчета.
     */
    const fetchReport = async () => {
       
        setLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const response = await axios.get(
                '/api/timesheet/installer-report',
                {
                    params:
                        requestParams,

                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            const reportUser =
    response.data?.user ||
    null;

setCurrentUser(
    reportUser
);

setUserCounterparties(
    Array.isArray(
        reportUser?.counterparties
    )
        ? reportUser.counterparties
        : []
);

            setReport({
                summary:
                    response.data?.summary ||
                    {},

                tasks:
    response.data?.tasks ||
    [],

unfinished:
    response.data?.unfinished ||
    [],

estimated:
    response.data?.estimated ||
    [],

preliminary:
    response.data?.preliminary ||
    [],

final:
    response.data?.final ||
    [],

                charts:
                    response.data?.charts ||
                    {
                        daily: [],
                        weekly: [],
                        monthly: []
                    },

                options:
                    response.data?.options ||
                    {
                        ttNumbers: [],
                        workTypes: [],
                        statuses: [],
                        assignees: []
                    }
            });
        } catch (error) {
            console.error(
                'Ошибка загрузки табеля:',
                error
            );

            message.error(
                error.response?.data?.message ||
                'Не удалось загрузить кабинет монтажника'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [
        dateRange,
        filters.ttNumber,
        filters.workType,
        filters.status
    ]);

    /**
     * Быстрый выбор периода.
     */
    const handleQuickPeriod = value => {
        setQuickPeriod(value);

        if (value === 'week') {
            setDateRange([
                dayjs().startOf('week'),
                dayjs().endOf('week')
            ]);
        }

        if (value === 'month') {
            setDateRange([
                dayjs().startOf('month'),
                dayjs().endOf('month')
            ]);
        }
    };

    /**
     * Сброс фильтров.
     */
    const resetFilters = () => {
        setFilters({
            search: '',
            ttNumber: undefined,
            workType: undefined,
            status: undefined,
            assignee: undefined
        });

        setQuickPeriod('month');

        setDateRange(
            DEFAULT_DATE_RANGE
        );
    };

    const openTaskModal = async task => {
    if (
        !task?.externalId
    ) {
        message.warning(
            'У задания отсутствует внешний ID'
        );

        return;
    }

    setSelectedTask(
        task
    );

    setExternalTaskData(
        null
    );

    setTaskModalOpen(
        true
    );

    setTaskModalLoading(
        true
    );

    try {
        const token =
            localStorage.getItem(
                'token'
            );

        const response =
            await axios.get(
                `/api/tasks/ext/${
                    encodeURIComponent(
                        task.externalId
                    )
                }`,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        setExternalTaskData(
            response.data ||
            null
        );
    } catch (error) {
        console.error(
            'Ошибка загрузки полной информации задания:',
            error
        );

        message.error(
            error.response?.data?.message ||
            error.response?.data?.error ||
            'Не удалось загрузить информацию задания'
        );
    } finally {
        setTaskModalLoading(
            false
        );
    }
};

const closeTaskModal = () => {
    setTaskModalOpen(
        false
    );

    setSelectedTask(
        null
    );

    setExternalTaskData(
        null
    );

    setTaskModalLoading(
        false
    );
};





/**
 * Безопасно разбирает JSON.
 */
const safeParseJson = value => {
    if (!value) {
        return {};
    }

    if (
        typeof value ===
        'object'
    ) {
        return value;
    }

    try {
        return JSON.parse(
            value
        );
    } catch (error) {
        return {};
    }
};

/**
 * Полный набор custom_fields задачи.
 */
const getTaskCustomFields = taskData => {
    if (!taskData) {
        return {};
    }

    return safeParseJson(
        taskData.custom_fields
    );
};

/**
 * Получает значение custom field
 * без учёта регистра его системного имени.
 */
const getTaskCustomField = (
    taskData,
    fieldName
) => {
    const customFields =
        getTaskCustomFields(
            taskData
        );

    const fieldKey =
        Object.keys(
            customFields
        ).find(
            key =>
                String(key)
                    .toLowerCase() ===
                String(fieldName)
                    .toLowerCase()
        );

    if (!fieldKey) {
        return null;
    }

    const field =
        customFields[
            fieldKey
        ];

    if (
        field &&
        typeof field ===
            'object' &&
        Object.prototype
            .hasOwnProperty
            .call(
                field,
                'value'
            )
    ) {
        return field.value;
    }

    return field;
};



/**
 * Форматирует пустые значения.
 */
const formatTaskValue = value => {
    if (
        value === undefined ||
        value === null ||
        value === ''
    ) {
        return '—';
    }

    if (
        typeof value ===
        'object'
    ) {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            JSON.stringify(value)
        );
    }

    return String(value);
};

/**
 * Форматирует дату так же, как TasksPage.
 *
 * Поддерживает:
 * - Unix timestamp в секундах;
 * - Unix timestamp в миллисекундах;
 * - DD.MM.YYYY HH:mm:ss;
 * - DD.MM.YYYY HH:mm;
 * - ISO.
 */
const formatTaskDateTime = value => {
    if (
        value === undefined ||
        value === null ||
        value === '' ||
        value === false ||
        value === 'false'
    ) {
        return '—';
    }

    try {
        const text =
            String(value).trim();

        if (!text) {
            return '—';
        }

        /**
         * Unix timestamp.
         */
        if (/^\d+$/.test(text)) {
            const numericValue =
                text.length === 10
                    ? Number(text) * 1000
                    : Number(text);

            let date =
                dayjs(numericValue);

            if (!date.isValid()) {
                return '—';
            }

            /**
             * Полностью повторяем фиксацию МСК
             * из TasksPage.
             */
            const currentOffset =
                date.utcOffset();

            const moscowOffset =
                180;

            date =
                date.add(
                    moscowOffset -
                        currentOffset,
                    'minute'
                );

            return date.format(
                'DD.MM.YYYY HH:mm'
            );
        }

        /**
         * Формат DD.MM.YYYY HH:mm:ss.
         */
        const russianDateMatch =
            text.match(
                /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
            );

        if (russianDateMatch) {
            const [
                ,
                day,
                month,
                year,
                hours,
                minutes,
                seconds = '0'
            ] = russianDateMatch;

            const parsed =
                dayjs(
                    `${year}-${month}-${day}T` +
                    `${String(hours).padStart(2, '0')}:` +
                    `${minutes}:` +
                    `${seconds}`
                );

            if (parsed.isValid()) {
                return parsed.format(
                    'DD.MM.YYYY HH:mm'
                );
            }
        }

        /**
         * ISO и обычные даты.
         */
        const parsed =
            dayjs(text);

        if (parsed.isValid()) {
            return parsed.format(
                'DD.MM.YYYY HH:mm'
            );
        }

        return text.substring(
            0,
            16
        );
    } catch (error) {
        return '—';
    }
};

/**
 * Стадия задания.
 */
const getTaskConfirmedTitle = value => {
    const stages = {
        0:
            'Отказано',

        1:
            'В работе',

        2:
            'Завершено'
    };

    return stages[value] ||
        formatTaskValue(value);
};



/**
 * Суммирует время дороги.
 */
const getTotalRoadMinutes = taskData => {
    const roadTo =
        Number(
            getTaskCustomField(
                taskData,
                'Doroga_do_obiekta_(minut)'
            )
        ) || 0;

    const roadFrom =
        Number(
            getTaskCustomField(
                taskData,
                'Doroga_s_obiekta_(minut)'
            )
        ) || 0;

    return roadTo +
        roadFrom;
};

const TaskInfoField = ({
    label,
    value
}) => {
    return (
        <div
            style={{
                marginBottom:
                    9
            }}
        >
            <Text
                type="secondary"
                style={{
                    display:
                        'block',

                    fontSize:
                        12,

                    marginBottom:
                        2
                }}
            >
                {label}
            </Text>

            <Text
                strong
                style={{
                    whiteSpace:
                        'pre-wrap',

                    wordBreak:
                        'break-word'
                }}
            >
                {formatTaskValue(
                    value
                )}
            </Text>
        </div>
    );
};

const TaskInfoBlock = ({
    children
}) => {
    return (
        <div
            style={{
                padding:
                    12,

                border:
                    '1px solid #e8e8e8',

                borderRadius:
                    8,

                background:
                    '#fafafa',

                height:
                    '100%',

                boxShadow:
                    '0 2px 4px rgba(0,0,0,0.02)'
            }}
        >
            {children}
        </div>
    );
};

    /**
     * Карточка задачи.
     */
   const renderTaskCard = task => {
    const taskTitle =
        task.title ||
        `Задача №${task.externalId}`;

    return (
        <Card
            hoverable
            className="installer-task-card"
            onClick={() =>
                openTaskModal(task)
            }
            key={
                task.id ||
                task.externalId
            }
            size="small"
            style={{
                display:
                    'block',

                width:
                    '100%',

                maxWidth:
                    '100%',

                minWidth:
                    0,

                marginBottom:
                    12,

                borderRadius:
                    10,

                boxSizing:
                    'border-box',

                overflow:
                    'hidden'
            }}
            bodyStyle={{
                display:
                    'block',

                width:
                    '100%',

                maxWidth:
                    '100%',

                minWidth:
                    0,

                padding:
                    12,

                boxSizing:
                    'border-box',

                overflow:
                    'hidden'
            }}
        >
            <div
                style={{
                    display:
                        'flex',

                    flexDirection:
                        'column',

                    width:
                        '100%',

                    maxWidth:
                        '100%',

                    minWidth:
                        0,

                    gap:
                        10,

                    boxSizing:
                        'border-box'
                }}
            >
                {/* Заголовок */}
                <div
                    style={{
                        display:
                            'flex',

                        flexDirection:
                            'column',

                        alignItems:
                            'flex-start',

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        minWidth:
                            0,

                        gap:
                            8
                    }}
                >
                    <Text
                        strong
                        style={{
                            display:
                                'block',

                            width:
                                '100%',

                            maxWidth:
                                '100%',

                            minWidth:
                                0,

                            fontSize:
                                15,

                            lineHeight:
                                1.4,

                            whiteSpace:
                                'normal',

                            overflowWrap:
                                'anywhere',

                            wordBreak:
                                'break-word'
                        }}
                    >
                        {taskTitle}
                    </Text>

                    <div
                        style={{
                            display:
                                'block',

                            width:
                                '100%',

                            maxWidth:
                                '100%',

                            minWidth:
                                0
                        }}
                    >
                        <TaskStageTag
                            stage={
                                task.status
                            }
                        />
                    </div>
                </div>

                {/* ID и номер ТТ */}
                <div
                    style={{
                        display:
                            'flex',

                        flexWrap:
                            'wrap',

                        alignItems:
                            'center',

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        minWidth:
                            0,

                        gap:
                            6
                    }}
                >
                    <Tag
                        style={{
                            maxWidth:
                                '100%',

                            marginInlineEnd:
                                0,

                            whiteSpace:
                                'normal',

                            overflowWrap:
                                'anywhere',

                            wordBreak:
                                'break-word'
                        }}
                    >
                        ID: {
                            task.externalId ||
                            '—'
                        }
                    </Tag>

                    <Tag
                        color="geekblue"
                        style={{
                            maxWidth:
                                '100%',

                            marginInlineEnd:
                                0,

                            whiteSpace:
                                'normal',

                            overflowWrap:
                                'anywhere',

                            wordBreak:
                                'break-word'
                        }}
                    >
                        ТТ: {
                            task.ttNumber ||
                            '—'
                        }
                    </Tag>
                </div>

                {/* Вид работ */}
                <Text
                    type="secondary"
                    style={{
                        display:
                            'block',

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        minWidth:
                            0,

                        whiteSpace:
                            'normal',

                        overflowWrap:
                            'anywhere',

                        wordBreak:
                            'break-word'
                    }}
                >
                    Вид работ: {
                        task.workType ||
                        '—'
                    }
                </Text>

                {/* Даты */}
                <div
                    style={{
                        display:
                            'grid',

                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(160px, 1fr))',

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        minWidth:
                            0,

                        gap:
                            8
                    }}
                >
                    <div
                        style={{
                            minWidth:
                                0,

                            padding:
                                10,

                            border:
                                '1px solid #f0f0f0',

                            borderRadius:
                                8,

                            background:
                                '#fafafa',

                            boxSizing:
                                'border-box'
                        }}
                    >
                        <Text
                            type="secondary"
                            style={{
                                display:
                                    'block',

                                marginBottom:
                                    3,

                                fontSize:
                                    12
                            }}
                        >
                            Появился на сайте
                        </Text>

                        <Text
                            strong
                            style={{
                                display:
                                    'block',

                                minWidth:
                                    0,

                                fontSize:
                                    14,

                                whiteSpace:
                                    'normal',

                                overflowWrap:
                                    'anywhere',

                                wordBreak:
                                    'break-word'
                            }}
                        >
                            {
                                formatTaskDateTime(
                                    task.appearanceAt
                                )
                            }
                        </Text>
                    </div>

                    <div
                        style={{
                            minWidth:
                                0,

                            padding:
                                10,

                            border:
                                '1px solid #f0f0f0',

                            borderRadius:
                                8,

                            background:
                                '#fafafa',

                            boxSizing:
                                'border-box'
                        }}
                    >
                        <Text
                            type="secondary"
                            style={{
                                display:
                                    'block',

                                marginBottom:
                                    3,

                                fontSize:
                                    12
                            }}
                        >
                            Покинул сайт
                        </Text>

                        <Text
                            strong
                            style={{
                                display:
                                    'block',

                                minWidth:
                                    0,

                                fontSize:
                                    14,

                                whiteSpace:
                                    'normal',

                                overflowWrap:
                                    'anywhere',

                                wordBreak:
                                    'break-word'
                            }}
                        >
                            {
                                formatTaskDateTime(
                                    task.leaveAt
                                )
                            }
                        </Text>
                    </div>
                </div>

                {/* Часы и состояние ВДР */}
                <div
                    style={{
                        display:
                            'flex',

                        flexWrap:
                            'wrap',

                        alignItems:
                            'center',

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        minWidth:
                            0,

                        gap:
                            6
                    }}
                >
                    <Tag
                        icon={
                            <ClockCircleOutlined />
                        }
                        color="blue"
                        style={{
                            maxWidth:
                                '100%',

                            marginInlineEnd:
                                0,

                            whiteSpace:
                                'normal'
                        }}
                    >
                        {
                            Number(
                                task.workedHours ||
                                0
                            ).toLocaleString(
                                'ru-RU'
                            )
                        } ч
                    </Tag>

                    {task.vdrId ? (
                        <Tag
                            color={
                                task.isClosedByAct
                                    ? 'green'
                                    : 'gold'
                            }
                            style={{
                                maxWidth:
                                    '100%',

                                marginInlineEnd:
                                    0,

                                whiteSpace:
                                    'normal',

                                overflowWrap:
                                    'anywhere',

                                wordBreak:
                                    'break-word'
                            }}
                        >
                            {
                                task.isClosedByAct
                                    ? 'Закрыто актом'
                                    : 'ВДР без акта'
                            }
                        </Tag>
                    ) : (
                        <Tag
                            style={{
                                maxWidth:
                                    '100%',

                                marginInlineEnd:
                                    0,

                                whiteSpace:
                                    'normal'
                            }}
                        >
                            Без ВДР
                        </Tag>
                    )}
                </div>

                {/* Описание */}
                {task.description && (
                    <div
                        onClick={event => {
                            /*
                             * Чтобы нажатие на Collapse
                             * не открывало модальное окно.
                             */
                            event.stopPropagation();
                        }}
                        style={{
                            width:
                                '100%',

                            maxWidth:
                                '100%',

                            minWidth:
                                0,

                            overflow:
                                'hidden'
                        }}
                    >
                        <Collapse
                            ghost
                            size="small"
                            style={{
                                width:
                                    '100%',

                                maxWidth:
                                    '100%',

                                minWidth:
                                    0
                            }}
                            items={[
                                {
                                    key:
                                        'description',

                                    label: (
                                        <Text
                                            style={{
                                                whiteSpace:
                                                    'normal',

                                                overflowWrap:
                                                    'anywhere',

                                                wordBreak:
                                                    'break-word'
                                            }}
                                        >
                                            Описание задачи
                                        </Text>
                                    ),

                                    children: (
                                        <Text
                                            style={{
                                                display:
                                                    'block',

                                                width:
                                                    '100%',

                                                maxWidth:
                                                    '100%',

                                                minWidth:
                                                    0,

                                                whiteSpace:
                                                    'pre-wrap',

                                                overflowWrap:
                                                    'anywhere',

                                                wordBreak:
                                                    'break-word'
                                            }}
                                        >
                                            {
                                                task.description
                                            }
                                        </Text>
                                    )
                                }
                            ]}
                        />
                    </div>
                )}
            </div>
        </Card>
    );
};

    /**
     * Карточка табеля.
     */
    const renderTimesheetCard = item => {
        return (
            <Card
                key={`${item.id}-${item.vdrId}`}
                size="small"
                style={{
                    marginBottom: 12,
                    borderRadius: 10
                }}
                bodyStyle={{
                    padding: 14
                }}
            >
                <Space
                    direction="vertical"
                    size={8}
                    style={{
                        width: '100%'
                    }}
                >
                    <Space
                        wrap
                        style={{
                            width: '100%',
                            justifyContent:
                                'space-between'
                        }}
                    >
                        <Text strong>
                            {item.title}
                        </Text>

                        <Tag
                            color={
                                item.isClosedByAct
                                    ? 'green'
                                    : 'gold'
                            }
                        >
                            {item.isClosedByAct
                                ? 'Табель'
                                : 'Предварительный'}
                        </Tag>
                    </Space>

                    <Space wrap>
                        <Tag>
                            Задача №{item.externalId}
                        </Tag>

                        <Tag color="geekblue">
                            ТТ: {item.ttNumber || '—'}
                        </Tag>
                    </Space>

                    <Text type="secondary">
                        Вид работ: {item.workType || '—'}
                    </Text>

                    <Row gutter={[8, 8]}>
                        <Col xs={12}>
                            <Statistic
                                title="Рабочих дней"
                                value={
                                    item.workingDays || 0
                                }
                                suffix="дн."
                            />
                        </Col>

                        <Col xs={12}>
                            <Statistic
                                title="Отработано"
                                value={formatMinutes(
                                    item.workedMinutes
                                )}
                                valueStyle={{
                                    fontSize: 17
                                }}
                            />
                        </Col>
                    </Row>

                    <Card
                        size="small"
                        style={{
                            background: '#fafafa'
                        }}
                    >
                        <Space
                            direction="vertical"
                            size={4}
                        >
                            <Text>
                                ВДР: {item.vdrNumber ||
                                    item.vdrId ||
                                    '—'}
                            </Text>

                            <Text>
                                Сумма ВДР: {
                                    formatMoney(
                                        item.vdrAmount
                                    )
                                }
                            </Text>

                            <Text>
                                Акт: {item.actNumber ||
                                    item.actId ||
                                    'Отсутствует'}
                            </Text>
                        </Space>
                    </Card>
                </Space>
            </Card>
        );
    };

    /**
     * Список карточек.
     */
    const renderCardsList = (
        items,
        renderFunction
    ) => {
        if (!items.length) {
            return (
                <Empty
                    description={
                        'Нет данных за выбранный период'
                    }
                />
            );
        }

        return (
    <div
        className="installer-task-list"
        style={{
            display:
                'block',

            width:
                '100%',

            maxWidth:
                '100%',

            minWidth:
                0,

            overflow:
                'hidden',

            boxSizing:
                'border-box'
        }}
    >
        {
            items.map(
                renderFunction
            )
        }
    </div>
);
    };

    /**
     * Данные активной диаграммы.
     */
    const chartData =
        report.charts?.[analyticsMode] ||
        [];

    /**
     * Вкладки кабинета.
     */
    const tabItems = [
    {
        key:
            'tasks',

        label: (
            <span>
                <FileSearchOutlined />
                {' '}Все задания
                <Tag
                    style={{
                        marginLeft: 6
                    }}
                >
                    {report.tasks.length}
                </Tag>
            </span>
        ),

        children:
            renderCardsList(
                report.tasks,
                renderTaskCard
            )
    },

    {
        key:
            'unfinished',

        label: (
            <span>
                <ClockCircleOutlined />
                {' '}Незавершённые задания
                <Tag
                    color="orange"
                    style={{
                        marginLeft: 6
                    }}
                >
                    {report.unfinished.length}
                </Tag>
            </span>
        ),

        children:
            renderCardsList(
                report.unfinished,
                renderTaskCard
            )
    },

    {
    key:
        'not-estimated',

    label: (
        <span>
            <ClockCircleOutlined />

            {' '}Не осмечено

            <Tag
                color="orange"
                style={{
                    marginLeft:
                        6
                }}
            >
                {
                    notEstimatedTasks
                        .length
                }
            </Tag>
        </span>
    ),

    children:
        renderCardsList(
            notEstimatedTasks,
            renderTaskCard
        )
},

    {
        key:
            'estimated',

        label: (
            <span>
                <CheckCircleOutlined />
                {' '}Осмеченные задания
                <Tag
                    color="blue"
                    style={{
                        marginLeft: 6
                    }}
                >
                    {report.estimated.length}
                </Tag>
            </span>
        ),

        children:
            renderCardsList(
                report.estimated,
                renderTaskCard
            )
    },

    {
        key:
            'preliminary',

        label: (
            <span>
                <FileSearchOutlined />
                {' '}Предварительный заказ
                <Tag
                    color="gold"
                    style={{
                        marginLeft: 6
                    }}
                >
                    {report.preliminary.length}
                </Tag>
            </span>
        ),

        children:
            renderCardsList(
                report.preliminary,
                renderTaskCard
            )
    },

    {
        key:
            'final',

        label: (
            <span>
                <FileDoneOutlined />
                {' '}Акт
                <Tag
                    color="green"
                    style={{
                        marginLeft: 6
                    }}
                >
                    {report.final.length}
                </Tag>
            </span>
        ),

        children:
            renderCardsList(
                report.final,
                renderTaskCard
            )
    },

    {
        key: 'tmc',
        label: (
            <span>
                <InboxOutlined />
                {' '}Моё ТМЦ
            </span>
        ),
        children: <InstallerTmcPanel />
    }
];

    return (
    <Layout
        className="installer-timesheet-page"
        style={{
            width:
                '100%',

            maxWidth:
                '100%',

            minWidth:
                0,

           overflowX:
            'clip',

        boxSizing:
            'border-box'
        }}
    >
            <div
            className="installer-page-content"
    style={{
        width:
            '100%',

        maxWidth:
            1400,

        minWidth:
            0,

        margin:
            '0 auto',

        padding:
            '12px 12px 24px',

        boxSizing:
            'border-box',

        overflowX:
            'hidden'
    }}
>
                <div
    className="installer-page-stack"
    style={{
        display:
            'flex',

        flexDirection:
            'column',

        width:
            '100%',

        maxWidth:
            '100%',

        minWidth:
            0,

        gap:
            12,

        overflowX:
            'clip',

        boxSizing:
            'border-box',

        contain:
            'inline-size'
    }}
>
                    <Card
                        size="small"
                        bodyStyle={{
                            padding: 14
                        }}
                    >
                        <Space
                            direction="vertical"
                            size={6}
                            style={{
                                width: '100%'
                            }}
                        >
                            <Title
                                level={3}
                                style={{
                                    margin: 0
                                }}
                            >
                                Кабинет исполнителя
                            </Title>

                            {currentUser && (
                                <Text type="secondary">
                                    {[
                                        currentUser.last_name,
                                        currentUser.first_name
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                </Text>
                            )}

                            <Link to="/installer/work">
                                <Button type="primary" block>
                                    Новый кабинет (работа)
                                </Button>
                            </Link>

                            <InstallerOutboxBadge />
                        </Space>
                    </Card>

                    <Segmented
                        block
                        value={quickPeriod}
                        onChange={handleQuickPeriod}
                        options={[
                            {
                                label: 'Неделя',
                                value: 'week'
                            },
                            {
                                label: 'Месяц',
                                value: 'month'
                            },
                            {
                                label: 'Период',
                                value: 'custom'
                            }
                        ]}
                    />

                    <RangePicker
                        value={dateRange}
                        format="DD.MM.YYYY"
                        style={{
                            width: '100%'
                        }}
                        onChange={value => {
                            setQuickPeriod('custom');

                            setDateRange(
                                value ||
                                DEFAULT_DATE_RANGE
                            );
                        }}
                    />

                    <Card
                        size="small"
                        bodyStyle={{
                            padding: 12
                        }}
                    >
                        <Space
                            direction="vertical"
                            size={10}
                            style={{
                                width: '100%'
                            }}
                        >
                            <Input.Search
                                allowClear
                                enterButton={
                                    <SearchOutlined />
                                }
                                placeholder={
                                    'Название или номер задачи'
                                }
                                value={
                                    filters.search
                                }
                                onChange={event => {
                                    setFilters(current => ({
                                        ...current,
                                        search:
                                            event.target.value
                                    }));
                                }}
                                onSearch={fetchReport}
                            />

                            <Select
                                allowClear
                                showSearch
                                placeholder="Номер ТТ"
                                style={{
                                    width: '100%'
                                }}
                                value={
                                    filters.ttNumber
                                }
                                options={
                                    (
                                        report.options
                                            ?.ttNumbers ||
                                        []
                                    ).map(value => ({
                                        label: value,
                                        value
                                    }))
                                }
                                onChange={value => {
                                    setFilters(current => ({
                                        ...current,
                                        ttNumber: value
                                    }));
                                }}
                            />

                            <Select
                                allowClear
                                showSearch
                                placeholder="Вид работ"
                                style={{
                                    width: '100%'
                                }}
                                value={
                                    filters.workType
                                }
                                options={
                                    (
                                        report.options
                                            ?.workTypes ||
                                        []
                                    ).map(value => ({
                                        label: value,
                                        value
                                    }))
                                }
                                onChange={value => {
                                    setFilters(current => ({
                                        ...current,
                                        workType: value
                                    }));
                                }}
                            />

                            <Select
                                allowClear
                                showSearch
                                placeholder="Статус"
                                style={{
                                    width: '100%'
                                }}
                                value={
                                    filters.status
                                }
                                options={
                                    (
                                        report.options
                                            ?.statuses ||
                                        []
                                    ).map(value => ({
                                        label: value,
                                        value
                                    }))
                                }
                                onChange={value => {
                                    setFilters(current => ({
                                        ...current,
                                        status: value
                                    }));
                                }}
                            />

                            <Row gutter={[8, 8]}>
                                <Col xs={12}>
                                    <Button
                                        block
                                        icon={
                                            <ReloadOutlined />
                                        }
                                        loading={loading}
                                        onClick={fetchReport}
                                    >
                                        Обновить
                                    </Button>
                                </Col>

                                <Col xs={12}>
                                    <Button
                                        block
                                        onClick={resetFilters}
                                    >
                                        Сбросить
                                    </Button>
                                </Col>
                            </Row>
                        </Space>
                    </Card>

                   {/* Сводка исполнителя за выбранный период */}
<Row
    gutter={[
        12,
        12
    ]}
>
    <Col
        xs={24}
    sm={12}
    lg={6}
    >
        <Card
            size="small"
            style={{
                height:
                    '100%',

                borderRadius:
                    12
            }}
            bodyStyle={{
                padding:
                    16
            }}
        >
            <Statistic
                title="Дней в работе"
                value={
                    Number(
                        report.summary
                            ?.workingDays ||
                        0
                    )
                }
                suffix="дн."
                prefix={
                    <CalendarOutlined />
                }
                valueStyle={{
                    fontSize:
                        26
                }}
            />

            <Text
                type="secondary"
                style={{
                    display:
                        'block',

                    marginTop:
                        8,

                    fontSize:
                        12
                }}
            >
                Количество дней, в которые исполнитель
                выезжал на задания
            </Text>
        </Card>
    </Col>

    <Col
        xs={24}
    sm={12}
    lg={6}
    >
        <Card
            size="small"
            style={{
                height:
                    '100%',

                borderRadius:
                    12
            }}
            bodyStyle={{
                padding:
                    16
            }}
        >
            <Statistic
                title="В работе часов"
                value={
                    Number(
                        report.summary
                            ?.workedHours ||
                        0
                    )
                }
                precision={2}
                suffix="ч"
                prefix={
                    <ClockCircleOutlined />
                }
                valueStyle={{
                    fontSize:
                        26
                }}
            />

            <Text
                type="secondary"
                style={{
                    display:
                        'block',

                    marginTop:
                        8,

                    fontSize:
                        12
                }}
            >
                Общее количество часов за выбранный период
            </Text>
        </Card>
    </Col>

    <Col
    xs={24}
    sm={12}
    lg={6}
>
    <Card
        size="small"
        style={{
            height: '100%',
            borderRadius: 12
        }}
        bodyStyle={{
            padding: 16
        }}
    >
        <Statistic
            title="Среднее без генерации"
            value={
                averageHoursStats
                    .regular
                    .averageHoursPerDay
            }
            precision={2}
            suffix="ч/день"
            prefix={
                <BarChartOutlined />
            }
            valueStyle={{
                fontSize: 26
            }}
        />

        <Text
            type="secondary"
            style={{
                display: 'block',
                marginTop: 8,
                fontSize: 12
            }}
        >
            Часы без вида работ «Генерация»,
            разделённые на дни выезда без генерации
        </Text>

        <Text
            type="secondary"
            style={{
                display: 'block',
                marginTop: 6,
                fontSize: 12
            }}
        >
            {
                averageHoursStats
                    .regular
                    .totalHours
                    .toLocaleString(
                        'ru-RU',
                        {
                            maximumFractionDigits: 2
                        }
                    )
            } ч / {
                averageHoursStats
                    .regular
                    .workingDays
            } дн.
        </Text>
    </Card>
</Col>
<Col
    xs={24}
    sm={12}
    lg={6}
>
    <Card
        size="small"
        style={{
            height: '100%',
            borderRadius: 12
        }}
        bodyStyle={{
            padding: 16
        }}
    >
        <Statistic
            title="Среднее по генерации"
            value={
                averageHoursStats
                    .generation
                    .averageHoursPerDay
            }
            precision={2}
            suffix="ч/день"
            prefix={
                <BarChartOutlined />
            }
            valueStyle={{
                fontSize: 26
            }}
        />

        <Text
            type="secondary"
            style={{
                display: 'block',
                marginTop: 8,
                fontSize: 12
            }}
        >
            Часы только по виду работ «Генерация»,
            разделённые на дни выезда по генерации
        </Text>

        <Text
            type="secondary"
            style={{
                display: 'block',
                marginTop: 6,
                fontSize: 12
            }}
        >
            {
                averageHoursStats
                    .generation
                    .totalHours
                    .toLocaleString(
                        'ru-RU',
                        {
                            maximumFractionDigits: 2
                        }
                    )
            } ч / {
                averageHoursStats
                    .generation
                    .workingDays
            } дн.
        </Text>
    </Card>
</Col>
</Row>



                    {loading ? (
                        <div
                            style={{
                                padding: 60,
                                textAlign: 'center'
                            }}
                        >
                            <Spin size="large" />
                        </div>
                    ) : (
                        
                       <div
    className="installer-tabs-container"
    style={{
        display:
            'block',

        width:
            '100%',

        maxWidth:
            '100%',

        minWidth:
            0,

        overflowX:
            'clip',

        boxSizing:
            'border-box',

        contain:
            'inline-size'
    }}
>

    <Tabs
    className="installer-tasks-tabs"
        activeKey={
            activeTab
        }
        onChange={
            setActiveTab
        }
        items={
            tabItems
        }
        size="small"
        tabBarGutter={
            10
        }
        moreIcon={
            <span>
                Ещё
            </span>
        }
        style={{
    display:
        'block',

    width:
        '100%',

    maxWidth:
        '100%',

    minWidth:
        0,

    overflow:
        'hidden',

    boxSizing:
        'border-box'
}}
    />
</div>
                    )}
                </div>
            </div>
            <Modal
    title={
        <Space
            direction="vertical"
            size={0}
            style={{
    width:
        '100%',

    maxWidth:
        '100%',

    minWidth:
        0
}}
        >
            <Text strong>
                Задание №
                {selectedTask
                    ?.externalId ||
                    '—'}
            </Text>

            <Text
    type="secondary"
    style={{
        display:
            'block',

        maxWidth:
            'calc(100vw - 90px)',

        fontSize:
            12,

        whiteSpace:
            'normal',

        overflowWrap:
            'anywhere',

        wordBreak:
            'break-word'
    }}
>
                {externalTaskData
                    ?.title ||
                    selectedTask
                        ?.title ||
                    'Просмотр задания'}
            </Text>
        </Space>
    }
    open={
        taskModalOpen
    }
    onCancel={
        closeTaskModal
    }
        width={
        1200
    }

    centered

    style={{
        maxWidth:
            'calc(100vw - 16px)',

        paddingBottom:
            0
    }}

    bodyStyle={{
        maxHeight:
            'calc(100vh - 150px)',

        maxWidth:
            '100%',

        minWidth:
            0,

        padding:
            12,

        overflowY:
            'auto',

        overflowX:
            'hidden',

        boxSizing:
            'border-box'
    }}
    footer={[
        <Button
            key="close"
            onClick={
                closeTaskModal
            }
        >
            Закрыть
        </Button>
    ]}
>
    <Spin
        spinning={
            taskModalLoading
        }
        tip="Загрузка информации задания..."
    >
        {!taskModalLoading &&
        !externalTaskData ? (
            <Empty
                description={
                    'Информация задания не загружена'
                }
            />
        ) : (
            externalTaskData && (
                <div
                    direction="vertical"
                    size={16}
                    style={{
                        width:
                            '100%'
                    }}
                >
                    <Collapse
                        defaultActiveKey={[
                            'basic'
                        ]}
                        ghost
                        items={[
                            {
                                key:
                                    'basic',

                                label: (
                                    <Text strong>
                                        Основная информация по заданию
                                    </Text>
                                ),

                                children: (
                                    <Row
                                        gutter={[
                                            16,
                                            16
                                        ]}
                                    >
                                        <Col
                                            xs={24}
                                            md={12}
                                            xl={6}
                                        >
                                            <TaskInfoBlock>
                                                <TaskInfoField
                                                    label="Заказчик"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Zakazchik'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Исполнитель"
                                                    value={
                                                        firstTaskValue(
                                                            externalTaskData
                                                                .assigned_user_fio,

                                                            externalTaskData
                                                                .assignee
                                                                ?.name,

                                                            selectedTask
                                                                ?.installer
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Соисполнитель"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'FIO_#1'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Соисполнитель 2"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'FIO_#3'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Номер ТТ"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Nomer_TT'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Объект (БС)"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Nomer_BS'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Родительская задача"
                                                    value={
                                                        firstTaskValue(
                                                            externalTaskData
                                                                .parent
                                                                ?.title,

                                                            externalTaskData
                                                                .parent
                                                                ?.id,

                                                            externalTaskData
                                                                .parent_id
                                                        )
                                                    }
                                                />
                                            </TaskInfoBlock>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={12}
                                            xl={6}
                                        >
                                            <TaskInfoBlock>
                                                <TaskInfoField
                                                    label="Вид работ"
                                                    value={
                                                        firstTaskValue(
                                                            externalTaskData
                                                                .news_type_name,

                                                            externalTaskData
                                                                .type
                                                                ?.name,

                                                            selectedTask
                                                                ?.workType
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
    label="Этап"
    value={
        firstTaskValue(
            externalTaskData
                ?.status_name,

            externalTaskData
                ?.status
                ?.name,

            selectedTask
                ?.status
        )
    }
/>

                                                <TaskInfoField
                                                    label="Стадия"
                                                    value={
                                                        getTaskConfirmedTitle(
                                                            externalTaskData
                                                                .confirmed
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Адрес"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Adres_BS'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Дата создания"
                                                    value={
                                                        formatTaskDateTime(
                                                            firstTaskValue(
                                                                externalTaskData
                                                                    .news_date,

                                                                externalTaskData
                                                                    .created_at,

                                                                selectedTask
                                                                    ?.createdAt
                                                            )
                                                        )
                                                    }
                                                />
                                            </TaskInfoBlock>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={12}
                                            xl={6}
                                        >
                                            <TaskInfoBlock>
                                                <TaskInfoField
    label="Появился на сайте"
    value={
        formatTaskDateTime(
            getTaskCustomField(
                externalTaskData,
                'Vremja_pojavlenija_na_sayte'
            )
        )
    }
/>

<TaskInfoField
    label="Покинул сайт"
    value={
        formatTaskDateTime(
            getTaskCustomField(
                externalTaskData,
                'Vremja_pokidanija_sayta'
            )
        )
    }
/>

                                                <TaskInfoField
                                                    label="На объекте"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Vremja_na_rabote'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Дизеление"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'CHase_dizelenija'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Часов по ВДР"
                                                    value={
                                                        `${
                                                            Number(
                                                                selectedTask
                                                                    ?.workedHours ||
                                                                0
                                                            ).toLocaleString(
                                                                'ru-RU'
                                                            )
                                                        } ч`
                                                    }
                                                />
                                            </TaskInfoBlock>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={12}
                                            xl={6}
                                        >
                                            <TaskInfoBlock>
                                                <TaskInfoField
                                                    label="Туда, минут"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Doroga_do_obiekta_(minut)'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Обратно, минут"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Doroga_s_obiekta_(minut)'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Всего дорога"
                                                    value={
                                                        `${
                                                            getTotalRoadMinutes(
                                                                externalTaskData
                                                            )
                                                        } мин`
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Пробег, км"
                                                    value={
                                                        getTaskCustomField(
                                                            externalTaskData,
                                                            'Probeg,_km'
                                                        )
                                                    }
                                                />

                                                <TaskInfoField
                                                    label="Заполняющий ВДР"
                                                    value={
                                                        firstTaskValue(
                                                            externalTaskData
                                                                .filling_user_fio,

                                                            externalTaskData
                                                                .filling_user_name
                                                        )
                                                    }
                                                />
                                            </TaskInfoBlock>
                                        </Col>

                                        <Col
                                            span={24}
                                        >
                                            <Card
                                                size="small"
                                                title="Задание"
                                                style={{
                                                    background:
                                                        '#fffbe6',

                                                    borderColor:
                                                        '#ffe58f'
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        whiteSpace:
                                                            'pre-wrap'
                                                    }}
                                                >
                                                    {firstTaskValue(
                                                        externalTaskData
                                                            .text,

                                                        externalTaskData
                                                            .description,

                                                        selectedTask
                                                            ?.description,

                                                        'Нет данных'
                                                    )}
                                                </Text>
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <Card
                                                size="small"
                                                title="Описание выполненных работ"
                                                style={{
                                                    height:
                                                        '100%',

                                                    background:
                                                        '#fffbe6',

                                                    borderColor:
                                                        '#ffe58f'
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        whiteSpace:
                                                            'pre-wrap'
                                                    }}
                                                >
                                                    {getTaskCustomField(
                                                        externalTaskData,
                                                        'Opisanie_vepolnenneh_rabot'
                                                    ) ||
                                                    'Нет данных'}
                                                </Text>
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <Card
                                                size="small"
                                                title="Комментарии исполнителя (не для заказчика)"
                                                style={{
                                                    height:
                                                        '100%',

                                                    background:
                                                        '#fffbe6',

                                                    borderColor:
                                                        '#ffe58f'
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        whiteSpace:
                                                            'pre-wrap'
                                                    }}
                                                >
                                                    {getTaskCustomField(
                                                        externalTaskData,
                                                        'comments'
                                                    ) ||
                                                    'Нет данных'}
                                                </Text>
                                            </Card>
                                        </Col>
                                    </Row>
                                )
                            }
                        ]}
                    />
                    {isSelectedTaskEstimated && (
    <Card
        size="small"
        title={
            <Space wrap>
                <Text strong>
                    Расходная часть ВДР
                </Text>

                <Tag color="magenta">
                    Осмечено
                </Tag>

                <Tag>
                    {
                        selectedTaskExpenseRows
                            .length
                    } стр.
                </Tag>
            </Space>
        }
        style={{
    width:
        '100%',

    maxWidth:
        '100%',

    minWidth:
        0,

    overflow:
        'hidden'
}}
        bodyStyle={{
            padding:
                0
        }}
    >
        {selectedTaskExpenseRows.length ? (
            <div
    style={{
        width:
            '100%',

        maxWidth:
            '100%',

        minWidth:
            0,

        overflowX:
            'auto',

        WebkitOverflowScrolling:
            'touch'
    }}
>
            <Table
                dataSource={
                    selectedTaskExpenseRows
                }
                columns={
                    expenseInvoiceColumns
                }
                rowKey={invoice =>
                    invoice.id ||
                    `${
                        invoice.article ||
                        ''
                    }-${
                        invoice.rate_name ||
                        ''
                    }-${
                        invoice.counterparty ||
                        ''
                    }`
                }
                pagination={false}
                bordered
                size="small"
                scroll={{
                    x:
                        1450
                }}
                summary={pageData => {
                    const total =
                        pageData.reduce(
                            (
                                sum,
                                invoice
                            ) => {
                                const isIncluded =
                                    !(
                                        invoice
                                            .include ===
                                            false ||
                                        invoice
                                            .include ===
                                            0 ||
                                        invoice
                                            .include ===
                                            '0'
                                    );

                                if (!isIncluded) {
                                    return sum;
                                }

                                return (
                                    sum +
                                    (
                                        Number(
                                            invoice
                                                .amount
                                        ) ||
                                        0
                                    )
                                );
                            },
                            0
                        );

                    return (
                        <Table.Summary.Row
                            style={{
                                background:
                                    '#fafafa'
                            }}
                        >
                            <Table.Summary.Cell
                                index={0}
                                colSpan={7}
                                align="right"
                            >
                                <Text strong>
                                    Итого:
                                </Text>
                            </Table.Summary.Cell>

                            <Table.Summary.Cell
                                index={7}
                                align="right"
                            >
                                <Text strong>
                                    {
                                        formatInvoiceNumber(
                                            total,
                                            2
                                        )
                                    } ₽
                                </Text>
                            </Table.Summary.Cell>

                            <Table.Summary.Cell
                                index={8}
                                colSpan={3}
                            />
                        </Table.Summary.Row>
                    );
                }}
            />
            </div>
        ) : (
            <Empty
                image={
                    Empty
                        .PRESENTED_IMAGE_SIMPLE
                }
                description={
                    'Для исполнителя строки не найдены'
                }
                style={{
                    padding:
                        24
                }}
            />
        )}
    </Card>
)}
                    <Collapse
                        items={[
                            {
                                key:
                                    'documents',

                                label: (
                                    <Text strong>
                                        Документы и ВДР
                                    </Text>
                                ),

                                children: (
                                    <Row
                                        gutter={[
                                            12,
                                            12
                                        ]}
                                    >
                                        <Col
                                            xs={24}
                                            md={8}
                                        >
                                            <Card
                                                size="small"
                                            >
                                                <Statistic
                                                    title="Часов по расходной ВДР"
                                                    value={
                                                        Number(
                                                            selectedTask
                                                                ?.workedHours ||
                                                            0
                                                        )
                                                    }
                                                    suffix="ч"
                                                />
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={8}
                                        >
                                            <Card
                                                size="small"
                                            >
                                                <Statistic
                                                    title="Заказов поставщика"
                                                    value={
                                                        selectedTask
                                                            ?.supplierOrders
                                                            ?.length ||
                                                        0
                                                    }
                                                />
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={8}
                                        >
                                            <Card
                                                size="small"
                                            >
                                                <Statistic
                                                    title="Актов поставщика"
                                                    value={
                                                        selectedTask
                                                            ?.supplierActs
                                                            ?.length ||
                                                        0
                                                    }
                                                />
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <Card
                                                size="small"
                                                title="Предварительный заказ"
                                            >
                                                {selectedTask
                                                    ?.supplierOrders
                                                    ?.length ? (
                                                    <Space
                                                        direction="vertical"
                                                    >
                                                        {selectedTask
                                                            .supplierOrders
                                                            .map(order => (
                                                                <Tag
                                                                    key={
                                                                        order.id
                                                                    }
                                                                    color="gold"
                                                                >
                                                                    №
                                                                    {order.number ||
                                                                    order.id}
                                                                </Tag>
                                                            ))}
                                                    </Space>
                                                ) : (
                                                    <Text type="secondary">
                                                        Заказ отсутствует
                                                    </Text>
                                                )}
                                            </Card>
                                        </Col>

                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <Card
                                                size="small"
                                                title="Акт"
                                            >
                                                {selectedTask
                                                    ?.supplierActs
                                                    ?.length ? (
                                                    <Space
                                                        direction="vertical"
                                                    >
                                                        {selectedTask
                                                            .supplierActs
                                                            .map(act => (
                                                                <Tag
                                                                    key={
                                                                        act.id
                                                                    }
                                                                    color="green"
                                                                >
                                                                    №
                                                                    {act.number ||
                                                                    act.id}
                                                                </Tag>
                                                            ))}
                                                    </Space>
                                                ) : (
                                                    <Text type="secondary">
                                                        Акт отсутствует
                                                    </Text>
                                                )}
                                            </Card>
                                        </Col>
                                    </Row>
                                )
                            }
                        ]}
                    />
                </div>
            )
        )}
    </Spin>
</Modal>
       </Layout>
    );
};

export default InstallerTimesheetPage;